<template>
  <div class="h-screen overflow-hidden antialiased selection:bg-blue-500 selection:text-white" v-cloak>
    <!-- 过渡动画 -->
    <OpsSplashScreen v-if="showSplash" />
    
    <!-- 运维界面：只显示已登录后的内容，登录界面由 MainEntry 统一管理 -->
    <transition name="main-fade-in">
      <div v-if="isAuthenticated && !showSplash" class="h-full flex flex-col bg-[#0f172a] overflow-hidden relative">
      <!-- 背景渐变 -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900/95 to-[#020617] pointer-events-none"></div>
      <div class="absolute top-0 left-0 w-[720px] h-[420px] bg-gradient-to-br from-indigo-500/15 via-sky-500/8 to-transparent rounded-[100%] blur-[140px] pointer-events-none mix-blend-screen opacity-70"></div>
      <div class="absolute bottom-0 right-0 w-[640px] h-[520px] bg-gradient-to-tl from-purple-500/15 via-indigo-500/8 to-transparent rounded-full blur-[130px] pointer-events-none mix-blend-screen opacity-60"></div>

      <!-- 顶部导航栏 -->
      <div class="relative z-10 flex-shrink-0 bg-slate-900/60 backdrop-blur-md border-b border-slate-700/60 px-6 py-4">
        <div class="flex items-center justify-between">
          <!-- 左侧标题 -->
          <div class="flex items-center gap-3">
            <div class="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600/80 shadow-lg shadow-blue-500/30">
              <i class="ph-bold ph-lightning text-2xl"></i>
            </div>
            <div>
              <h1 class="text-xl font-bold text-white leading-tight">Smart Grid运维管理平台</h1>
              <p class="text-[11px] text-slate-400 mt-0.5 tracking-wide">Smart Grid Operations &amp; Monitoring</p>
            </div>
          </div>

          <!-- 右侧用户信息区 -->
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-3 px-3 py-1.5 rounded-2xl bg-slate-900/70 border border-slate-700/70 shadow-sm">
              <div class="relative">
                <div class="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-sm font-semibold text-white shadow-lg shadow-sky-500/40">
                  {{ userInitial }}
                </div>
                <span class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-slate-900"></span>
              </div>
              <div class="flex flex-col leading-tight">
                <span class="text-sm text-slate-100">
                  {{ currentUser?.name || currentUser?.username || '运维管理员' }}
                </span>
                <span class="text-[11px] text-slate-400 uppercase tracking-wide">
                  OP · Smart Grid
                </span>
              </div>
            </div>
            <button
              @click="handleLogout"
              class="px-4 py-2 text-sm bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 text-white rounded-xl transition-all duration-200 flex items-center gap-2 shadow-sm shadow-slate-900/40 border border-slate-500/60"
            >
              <i class="ph-bold ph-sign-out text-base"></i>
              <span>退出登录</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 主内容区域 -->
      <div class="relative z-10 flex-1 overflow-hidden overflow-y-auto custom-scrollbar">
        <OpsManagementPage />
      </div>

      <!-- Toast 容器 -->
      <ToastContainer :toasts="toasts" @remove="removeToast" />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useOpsAuth } from '@/composables/ops/useOpsAuth';
import { useOpsToast } from '@/composables/ops/useOpsToast';
import OpsManagementPage from '@/components/pages/OpsManagementPage.vue';
import ToastContainer from '@/components/common/ToastContainer.vue';
import OpsSplashScreen from '@/components/common/OpsSplashScreen.vue';

// 使用运维平台专用的认证和 Toast（完全独立于用户界面）
const { 
  isAuthenticated, 
  currentUser, 
  logout 
} = useOpsAuth();

const { toasts, addToast, removeToast } = useOpsToast();

const showSplash = ref(false);

// 用户名首字母头像
const userInitial = computed(() => {
  const name = currentUser.value?.name || currentUser.value?.username || '';
  return name ? name.charAt(0).toUpperCase() : 'O';
});

/**
 * 处理退出登录
 */
const handleLogout = async () => {
  await logout();
  addToast('已退出登录', 'info', 2000);
};

// 页面加载时检查登录状态
onMounted(async () => {
  // 确保清除用户界面的存储（如果存在），避免冲突
  // 使用 try-catch 避免在非用户界面环境中出错
  try {
    const userStorageUtil = await import('@/utils/storage');
    if (userStorageUtil && userStorageUtil.storageUtil) {
      const userStorageUser = userStorageUtil.storageUtil.getUser();
      // 如果用户界面存储中有op账号，清除它（op账号应该在op界面登录）
      if (userStorageUser && userStorageUser.role === 'op') {
        userStorageUtil.storageUtil.clearUser();
      }
    }
  } catch (error) {
    // 忽略错误，可能是在op界面环境中无法导入用户界面存储工具
    console.debug('[OpsApp] 无法访问用户界面存储工具（这是正常的）');
  }
  
  // 如果已登录但不是运维管理员，则退出登录
  if (isAuthenticated.value && currentUser.value?.role !== 'op') {
    await logout();
    addToast('您没有权限访问运维管理平台', 'error', 3000);
  }
  
  // 如果已登录且是运维管理员，显示过渡动画（3秒）
  if (isAuthenticated.value && currentUser.value?.role === 'op') {
    showSplash.value = true;
    setTimeout(() => {
      showSplash.value = false;
    }, 3000);
  }
});
</script>

<style scoped>
[v-cloak] {
  display: none;
}
</style>

<style scoped>
/* 主内容区域淡入动画 */
.main-fade-in-enter-active {
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
  transition-delay: 0.2s;
}

.main-fade-in-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.main-fade-in-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>

<style>
/* 运维应用内的滚动条样式，与主应用保持一致风格 */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(51, 65, 85, 0.6);
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #475569;
}
</style>

